import { normalizeLocationText } from "./locationSearch";
import type { CitySource, StreetSource } from "./types";
import { inferComparableOrigin } from "../domain/comparableLifecycle";
import type { MarketComparableDecision, MarketComparableOrigin, MarketComparableRejectionReason, MarketComparableRole, MarketContextGroup, MarketPropertySnapshot } from "../domain/types";

const LOCATION_METADATA_KEY = "__fisproLocation";
const MARKET_METADATA_KEY = "__fisproMarket";
const PROPERTY_MODEL_KEY = "__fisproPropertyModel";

type LocationMetadata = {
  cityNormalized?: string;
  citySource?: CitySource;
  municipalityId?: string;
  streetNormalized?: string;
  streetSource?: StreetSource;
  streetId?: string;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

export const locationMetadataFromSourceSnapshot = (value: unknown, city: string, street = ""): LocationMetadata => {
  const source = isRecord(value) ? value : {};
  const metadata = isRecord(source[LOCATION_METADATA_KEY]) ? source[LOCATION_METADATA_KEY] : {};
  return {
    cityNormalized: typeof metadata.cityNormalized === "string"
      ? metadata.cityNormalized
      : normalizeLocationText(city),
    citySource: metadata.citySource === "dataset" || metadata.citySource === "custom"
      ? metadata.citySource
      : undefined,
    municipalityId: typeof metadata.municipalityId === "string" ? metadata.municipalityId : undefined,
    streetNormalized: typeof metadata.streetNormalized === "string"
      ? metadata.streetNormalized
      : normalizeLocationText(street),
    streetSource: metadata.streetSource === "dataset" || metadata.streetSource === "custom"
      ? metadata.streetSource
      : street
        ? "custom"
        : undefined,
    streetId: typeof metadata.streetId === "string" ? metadata.streetId : undefined
  };
};

export const sourceSnapshotWithLocationMetadata = (
  value: Record<string, unknown> | undefined,
  metadata: LocationMetadata
): Record<string, unknown> => ({
  ...(value ?? {}),
  [LOCATION_METADATA_KEY]: metadata
});

export const sourceSnapshotWithoutLocationMetadata = (value: unknown): Record<string, unknown> => {
  if (!isRecord(value)) return {};
  const { [LOCATION_METADATA_KEY]: _locationMetadata, ...sourceSnapshot } = value;
  return sourceSnapshot;
};

export const marketMetadataFromSourceSnapshot = (value: unknown): {
  decision: MarketComparableDecision;
  rejectionReason?: MarketComparableRejectionReason;
  origin: MarketComparableOrigin;
  marketRole: MarketComparableRole;
  marketContextGroup?: MarketContextGroup;
  primaryQualificationReasons?: string[];
} => {
  const source = isRecord(value) ? value : {};
  const metadata = isRecord(source[MARKET_METADATA_KEY]) ? source[MARKET_METADATA_KEY] : {};
  return {
    decision: metadata.decision === "excluded" ? "excluded" : "included",
    origin: inferComparableOrigin(metadata.origin, source),
    marketRole: metadata.marketRole === "competition" ? "competition" : "primary",
    marketContextGroup: metadata.marketContextGroup === "same_condition" || metadata.marketContextGroup === "other_condition"
      ? metadata.marketContextGroup
      : undefined,
    primaryQualificationReasons: Array.isArray(metadata.primaryQualificationReasons)
      ? metadata.primaryQualificationReasons.filter((reason): reason is string => typeof reason === "string")
      : undefined,
    rejectionReason: typeof metadata.rejectionReason === "string"
      ? metadata.rejectionReason as MarketComparableRejectionReason
      : undefined
  };
};

export const sourceSnapshotWithMarketMetadata = (
  value: Record<string, unknown> | undefined,
  metadata: { decision: MarketComparableDecision; rejectionReason?: MarketComparableRejectionReason; origin: MarketComparableOrigin; marketRole: MarketComparableRole; marketContextGroup?: MarketContextGroup; primaryQualificationReasons?: string[] }
): Record<string, unknown> => ({
  ...(value ?? {}),
  [MARKET_METADATA_KEY]: metadata
});

export const propertyModelFromSourceSnapshot = (value: unknown): Partial<MarketPropertySnapshot> => {
  const source = isRecord(value) ? value : {};
  const metadata = isRecord(source[PROPERTY_MODEL_KEY]) ? source[PROPERTY_MODEL_KEY] : {};
  return {
    location: isRecord(metadata.location) ? metadata.location as MarketPropertySnapshot["location"] : undefined,
    areas: isRecord(metadata.areas) ? metadata.areas as MarketPropertySnapshot["areas"] : undefined,
    technical: isRecord(metadata.technical) ? metadata.technical as MarketPropertySnapshot["technical"] : undefined,
    equipment: isRecord(metadata.equipment) ? metadata.equipment as MarketPropertySnapshot["equipment"] : undefined,
    canonicalPropertyType: typeof metadata.canonicalPropertyType === "string"
      ? metadata.canonicalPropertyType as MarketPropertySnapshot["canonicalPropertyType"] : undefined,
    fieldProvenance: isRecord(metadata.fieldProvenance) ? metadata.fieldProvenance as MarketPropertySnapshot["fieldProvenance"] : undefined,
    totalFloors: typeof metadata.totalFloors === "number" ? metadata.totalFloors : undefined,
    buildingType: typeof metadata.buildingType === "string" ? metadata.buildingType : undefined
  };
};

export const sourceSnapshotWithPropertyModel = (
  value: Record<string, unknown> | undefined,
  comparable: MarketPropertySnapshot
): Record<string, unknown> => ({
  ...(value ?? {}),
  [PROPERTY_MODEL_KEY]: {
    location: comparable.location,
    areas: comparable.areas,
    technical: comparable.technical,
    equipment: comparable.equipment,
    canonicalPropertyType: comparable.canonicalPropertyType,
    fieldProvenance: comparable.fieldProvenance,
    totalFloors: comparable.totalFloors,
    buildingType: comparable.buildingType
  }
});
